import { NgClass, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, forwardRef, signal } from '@angular/core';
import { IconComponent } from '@examdojo/core/icon';
import { NodeViewComponent } from '@examdojo/core/tiptap';
import { v4 as uuid } from 'uuid';
import { GojsDiagramBlock, GojsDiagramModel } from '../../../model';
import { GojsDiagramViewerComponent } from '../../../gojs/gojs-diagram-viewer.component';
import { DragWithHandleOnlyDirective } from '../drag-with-handle-only.directive';
import { GojsDiagramEditorComponent } from './gojs-diagram-editor.component';
import { DEFAULT_GOJS_DIAGRAM_PROPS } from './gojs-diagram.model';

@Component({
  selector: 'dojo-gojs-diagram-node',
  standalone: true,
  imports: [NgClass, NgStyle, IconComponent, GojsDiagramViewerComponent, GojsDiagramEditorComponent],
  template: `
    <div class="gojs-diagram-wrapper" [ngClass]="customCss()" [ngStyle]="customStyle() ?? {}">
      <dojo-gojs-diagram-viewer
        [model]="model()"
        [layoutType]="layoutType() ?? 'tree'"
        [height]="height()"
        [hoverHighlight]="hoverHighlight() ?? false"
      />
      @if (editor().isEditable) {
        <button class="edit-btn" (click)="openEditor()" title="Edit GoJS Diagram">
          <dojo-icon icon="pen-to-square" [size]="14" />
        </button>
      }
    </div>

    @if (editor().isEditable) {
      <dojo-gojs-diagram-editor
        [model]="model()"
        [layoutType]="layoutType() ?? 'tree'"
        [height]="height()"
        [isOpen]="showEditor()"
        (modelChange)="onModelChange($event)"
        (editorClose)="closeEditor()"
      />
    }
  `,
  styles: `
    :host {
      @apply block my-4;

      &.ProseMirror-selectednode {
        background-color: transparent !important;
        outline: none !important;
      }
    }

    .gojs-diagram-wrapper {
      position: relative;

      &:hover .edit-btn {
        opacity: 1;
      }
    }

    .edit-btn {
      position: absolute;
      top: 4px;
      right: 4px;
      padding: 6px;
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.15s, background-color 0.15s;
      color: #6b7280;
      z-index: 10;

      &:hover {
        background: #f3f4f6;
        color: #374151;
      }
    }
  `,
  hostDirectives: [DragWithHandleOnlyDirective],
  providers: [
    {
      provide: NodeViewComponent,
      useExisting: forwardRef(() => GojsDiagramNodeComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GojsDiagramNodeComponent extends NodeViewComponent<GojsDiagramBlock> {
  readonly model = this.getProp('model');
  readonly layoutType = this.getProp('layout_type');
  readonly heightProp = this.getProp('height');
  readonly customCss = this.getProp('custom_css');
  readonly customStyle = this.getProp('custom_style');
  readonly hoverHighlight = this.getProp('hover_highlight');

  readonly height = computed(() => this.heightProp() ?? 300);
  readonly showEditor = signal(false);

  override getDefaults = (): GojsDiagramBlock => ({
    ...DEFAULT_GOJS_DIAGRAM_PROPS,
    id: uuid(),
  });

  openEditor(): void {
    this.showEditor.set(true);
  }

  closeEditor(): void {
    this.showEditor.set(false);
  }

  onModelChange(update: { model: GojsDiagramModel; layoutType?: string; height?: number }): void {
    this.updateProps({
      model: update.model,
      ...(update.layoutType !== null && { layout_type: update.layoutType as GojsDiagramBlock['layout_type'] }),
      ...(update.height !== null && { height: update.height }),
    });
  }
}
